import { Nullable } from '@ui/types/utility';

/**
 * Distinct hues that read well on the dark history list background. Used purely
 * as a visual grouping cue (the left stripe + badge dot) so adjacent events of
 * the same position can be associated at a glance. Not theme tokens — these are
 * arbitrary identity colors keyed off a hash.
 */
const GROUP_PALETTE = [
  '#2ECC71', // green
  '#3498DB', // blue
  '#9B59B6', // purple
  '#F1C40F', // yellow
  '#E74C3C', // red
  '#1ABC9C', // teal
  '#E67E22', // orange
  '#E84393', // pink
];

/**
 * Neutral grey for events that have no group (e.g. limit-order lifecycle events
 * until BE exposes orderHash). Signals "ungrouped" rather than a real identity.
 */
export const UNGROUPED_COLOR = '#5A5A5E';

/**
 * Deterministically map a hash to one of the palette colors via djb2, so the
 * same position always gets the same color within a session.
 */
export const getGroupHashColor = (hash: Nullable<string>): string => {
  if (!hash) return UNGROUPED_COLOR;

  let h = 5381;
  for (let i = 0; i < hash.length; i++) {
    h = ((h << 5) + h) ^ hash.charCodeAt(i);
  }

  return GROUP_PALETTE[Math.abs(h) % GROUP_PALETTE.length];
};

/**
 * Short, human-scannable label for a group hash, e.g. "#a3f9". Strips a leading
 * 0x and takes the first 4 hex chars. Returns "—" when ungrouped.
 */
export const shortenGroupHash = (hash: Nullable<string>): string => {
  if (!hash) return '—';
  const stripped = hash.toLowerCase().startsWith('0x') ? hash.slice(2) : hash;

  return `#${stripped.slice(0, 4)}`;
};
